import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { differenceInCalendarDays, parseISO } from "date-fns";
import { api } from "@/lib/api";
import SearchBar from "@/components/SearchBar";
import ListingCard from "@/components/ListingCard";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { SearchX } from "lucide-react";

const SKELETON_KEYS = ["s1", "s2", "s3", "s4", "s5", "s6", "s7", "s8"];

function resultSummary(isLoading, count, p, nights) {
  if (isLoading) return "Searching…";
  const where = p.location ? ` in “${p.location}”` : "";
  const when = nights ? ` · ${nights} nights` : "";
  return `${count} stay${count === 1 ? "" : "s"}${where}${when} · prices include every fee`;
}

function PriceFilters({ p, update }) {
  const [min, setMin] = useState(p.min_price || "");
  const [max, setMax] = useState(p.max_price || "");
  return (
    <div className="mt-6 flex flex-wrap items-center gap-3 border-b pb-6" data-testid="search-filters">
      <span className="text-sm font-medium text-stone-600">Price per night</span>
      <Input data-testid="filter-min-price" type="number" min="0" placeholder="Min $" value={min} onChange={(e) => setMin(e.target.value)} className="w-28 rounded-full" />
      <Input data-testid="filter-max-price" type="number" min="0" placeholder="Max $" value={max} onChange={(e) => setMax(e.target.value)} className="w-28 rounded-full" />
      <Button data-testid="filter-apply-button" variant="outline" className="rounded-full" onClick={() => update({ min_price: min, max_price: max })}>Apply</Button>
      <div className="ml-auto">
        <Select value={p.sort || "recommended"} onValueChange={(v) => update({ sort: v === "recommended" ? "" : v })}>
          <SelectTrigger className="w-48 rounded-full" data-testid="filter-sort-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="recommended">Newest</SelectItem>
            <SelectItem value="price_asc">Price: low to high</SelectItem>
            <SelectItem value="price_desc">Price: high to low</SelectItem>
            <SelectItem value="rating">Top rated</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

const EmptyResults = () => (
  <div className="py-24 text-center" data-testid="search-empty">
    <SearchX className="mx-auto mb-4 h-10 w-10 text-stone-400" />
    <p className="font-semibold">No stays match those filters</p>
    <p className="text-sm text-stone-500">Try different dates or a wider area.</p>
  </div>
);

export default function Search() {
  const [params, setParams] = useSearchParams();
  const p = Object.fromEntries(params.entries());
  const nights = p.check_in && p.check_out ? differenceInCalendarDays(parseISO(p.check_out), parseISO(p.check_in)) : 0;

  const { data = [], isLoading } = useQuery({
    queryKey: ["listings", p],
    queryFn: () => api.get("/listings", { params: p }).then((r) => r.data),
  });

  const update = (patch) => {
    const next = { ...p, ...patch };
    Object.keys(next).forEach((k) => !next[k] && delete next[k]);
    setParams(next);
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      <SearchBar initial={p} compact key={params.toString()} />
      <PriceFilters p={p} update={update} />
      <p className="mt-6 text-sm text-stone-600" data-testid="search-result-count">{resultSummary(isLoading, data.length, p, nights)}</p>
      <div className="mt-6 grid gap-x-6 gap-y-10 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-testid="search-results">
        {isLoading && SKELETON_KEYS.map((k) => <Skeleton key={k} className="aspect-[4/3] rounded-2xl" />)}
        {data.map((l) => <ListingCard key={l.listing_id} l={l} nights={nights} />)}
      </div>
      {!isLoading && data.length === 0 && <EmptyResults />}
    </div>
  );
}
